import { timeToX, xToTime, isXWithinCycle, isXNearCycleEdge, isWithinCycleBar } from '../utils/coordinate-math';
import {
	INTERACTION_PROPERTIES,
	DEFAULT_CONFIG,
	CYCLE_EDGE,
	CURSOR_TYPES,
	MOUSE_ACTION_TYPE,
	DRAG_INTERACTIONS,
	ONE_MILLISECOND
} from '../utils/constants';
import { getMinCycleDuration } from '../utils/duration-utils';
import { roundToMilliseconds } from '../utils';
import { getNextValidStart } from '../utils/interval-helpers';
import {
	CycleData,
	InteractionPermissions,
	InteractionResult,
	CursorResult,
	CycleCheck,
	InteractionType,
	UnidentifiedCycleResult,
	MouseDownAction,
	CycleSnapResult
} from '../types';

function isNearEdge(
	mouseX: number,
	cycle: CycleData,
	edge: typeof CYCLE_EDGE.START | typeof CYCLE_EDGE.END,
	videoDuration: number,
	canvasWidth: number
): boolean {
	const margin = DEFAULT_CONFIG.MARGIN;
	const threshold = INTERACTION_PROPERTIES.HANDLE_THRESHOLD;
	const edgeType = edge === CYCLE_EDGE.START ? 'start' : 'end';
	return isXNearCycleEdge(mouseX, cycle, videoDuration, canvasWidth, threshold, edgeType, margin);
}

function checkCycleInteraction(
	mouseX: number,
	cycle: CycleData,
	videoDuration: number,
	canvasWidth: number
): CycleCheck {
	const margin = DEFAULT_CONFIG.MARGIN;

	const isNearStart = isNearEdge(mouseX, cycle, CYCLE_EDGE.START, videoDuration, canvasWidth);
	const isNearEnd = isNearEdge(mouseX, cycle, CYCLE_EDGE.END, videoDuration, canvasWidth);
	const isMouseOnCycle = isXWithinCycle(mouseX, cycle, videoDuration, canvasWidth, margin);

	const startX = timeToX(cycle.start, videoDuration, canvasWidth, margin);
	const dragOffset = mouseX - startX;

	return { cycle, isNearStart, isNearEnd, isMouseOnCycle, dragOffset };
}

function getClosestCycle(cycles: CycleData[], mouseX: number, videoDuration: number, canvasWidth: number): CycleData {
	const margin = DEFAULT_CONFIG.MARGIN;

	const closestCycle = cycles.reduce((closest, current) => {
		const closestCenter = (closest.start + closest.end) / 2;
		const currentCenter = (current.start + current.end) / 2;

		const closestX = timeToX(closestCenter, videoDuration, canvasWidth, margin);
		const currentX = timeToX(currentCenter, videoDuration, canvasWidth, margin);

		const closestDistance = Math.abs(closestX - mouseX);
		const currentDistance = Math.abs(currentX - mouseX);

		return currentDistance < closestDistance ? current : closest;
	});

	return closestCycle;
}

function getTargetCycle(
	cycles: CycleData[],
	mouseX: number,
	videoDuration: number,
	canvasWidth: number,
	editingCycleId?: string | null
): CycleData {
	const editableCycles = cycles.filter((cycle) => cycle.editable === true);

	if (editableCycles.length > 0) {
		return getClosestCycle(editableCycles, mouseX, videoDuration, canvasWidth);
	}

	const selectedCycle = editingCycleId ? cycles.find((cycle) => cycle.id === editingCycleId) : undefined;

	if (selectedCycle) {
		return selectedCycle;
	}

	return getClosestCycle(cycles, mouseX, videoDuration, canvasWidth);
}

function isCycleEditable(cycle: CycleData, editingCycleId?: string | null): boolean {
	if (cycle.editable !== undefined) {
		return cycle.editable;
	}

	return editingCycleId === cycle.id;
}

export function createDefaultPermissions(isInEditMode = false, editingCycleId?: string | null): InteractionPermissions {
	return { isInEditMode, editingCycleId };
}

function snapToAdjacentCycles(
	time: number,
	cycles: CycleData[],
	currentCycleId: string,
	isStart: boolean,
	snapThreshold: number = 0.5
): number {
	for (const cycle of cycles) {
		if (cycle.id === currentCycleId) continue;

		if (isStart) {
			const expectedStart = getNextValidStart(cycle.end);
			const distance = Math.abs(time - expectedStart);
			if (distance <= snapThreshold) {
				return expectedStart;
			}
		} else {
			const expectedEnd = cycle.start - ONE_MILLISECOND;
			const distance = Math.abs(time - expectedEnd);
			if (distance <= snapThreshold) {
				return expectedEnd;
			}
		}
	}

	return roundToMilliseconds(time);
}

function clampStart(start: number, maxStart: number): number {
	const clampedStart = Math.max(0, Math.min(start, maxStart));

	return clampedStart;
}

function clampEnd(end: number, videoDuration: number): number {
	const clampedEnd = Math.min(videoDuration, Math.max(end, 0));

	return clampedEnd;
}

function calculateMovePosition(
	mouseX: number,
	dragOffset: number,
	cycle: CycleData,
	canvasWidth: number,
	videoDuration: number,
	allCycles: CycleData[]
): { start: number; end: number } {
	const margin = DEFAULT_CONFIG.MARGIN;
	const cycleDuration = cycle.end - cycle.start;
	const adjustedStartX = mouseX - dragOffset;
	const newStartTime = xToTime(adjustedStartX, videoDuration, canvasWidth, margin);

	const snappedStart = snapToAdjacentCycles(newStartTime, allCycles, cycle.id, true);
	const clampedStart = clampStart(snappedStart, videoDuration - cycleDuration);

	const newStart = roundToMilliseconds(clampedStart);
	const newEnd = clampEnd(roundToMilliseconds(clampedStart + cycleDuration), videoDuration);

	return { start: newStart, end: newEnd };
}

export function calculateNewPosition(
	interactionType: InteractionType,
	mouseX: number,
	cycle: CycleData,
	dragOffset: number,
	canvasWidth: number,
	videoDuration: number,
	allCycles: CycleData[]
): { start: number; end: number } {
	const margin = DEFAULT_CONFIG.MARGIN;

	if (interactionType === InteractionType.START_RESIZE) {
		const newStartTime = xToTime(mouseX, videoDuration, canvasWidth, margin);
		const minDuration = getMinCycleDuration(videoDuration);

		const snappedStart = snapToAdjacentCycles(newStartTime, allCycles, cycle.id, true);
		const clampedStart = Math.max(0, Math.min(snappedStart, cycle.end - minDuration));

		return { start: clampedStart, end: cycle.end };
	}

	if (interactionType === InteractionType.END_RESIZE) {
		const newEndTime = xToTime(mouseX, videoDuration, canvasWidth, margin);
		const minDuration = getMinCycleDuration(videoDuration);

		const snappedEnd = snapToAdjacentCycles(newEndTime, allCycles, cycle.id, false);
		const clampedEnd = Math.min(videoDuration, Math.max(snappedEnd, cycle.start + minDuration));

		return { start: cycle.start, end: clampedEnd };
	}

	if (interactionType === InteractionType.MOVE) {
		return calculateMovePosition(mouseX, dragOffset, cycle, canvasWidth, videoDuration, allCycles);
	}

	return { start: cycle.start, end: cycle.end };
}

function handleUnidentifiedCycle(
	mouseX: number,
	cycles: CycleData[],
	videoDuration: number,
	canvasWidth: number
): UnidentifiedCycleResult | null {
	const unidentifiedCycle = cycles.find((cycle) => !cycle.identified);

	if (!unidentifiedCycle) {
		return null;
	}

	const check = checkCycleInteraction(mouseX, unidentifiedCycle, videoDuration, canvasWidth);

	if (check.isNearStart) {
		return {
			interaction: { type: InteractionType.START_RESIZE, cycleId: unidentifiedCycle.id },
			cursor: CURSOR_TYPES.EW_RESIZE,
			cycleId: unidentifiedCycle.id
		};
	}

	if (check.isNearEnd) {
		return {
			interaction: { type: InteractionType.END_RESIZE, cycleId: unidentifiedCycle.id },
			cursor: CURSOR_TYPES.EW_RESIZE,
			cycleId: unidentifiedCycle.id
		};
	}

	if (check.isMouseOnCycle) {
		return {
			interaction: { type: InteractionType.MOVE, cycleId: unidentifiedCycle.id, dragOffset: check.dragOffset },
			cursor: CURSOR_TYPES.GRAB,
			cycleId: unidentifiedCycle.id
		};
	}

	return { interaction: undefined };
}

function determineIdentifiedCycleInteraction(
	mouseX: number,
	cycle: CycleData,
	isSelected: boolean,
	videoDuration: number,
	canvasWidth: number
): InteractionResult {
	const check = checkCycleInteraction(mouseX, cycle, videoDuration, canvasWidth);
	const canDragResize = isSelected;

	if (check.isNearStart) {
		return canDragResize
			? { type: InteractionType.START_RESIZE, cycleId: cycle.id }
			: { type: InteractionType.SELECT, cycleId: cycle.id };
	}

	if (check.isNearEnd) {
		return canDragResize
			? { type: InteractionType.END_RESIZE, cycleId: cycle.id }
			: { type: InteractionType.SELECT, cycleId: cycle.id };
	}

	if (canDragResize) {
		return { type: InteractionType.MOVE, cycleId: cycle.id, dragOffset: check.dragOffset };
	}

	return { type: InteractionType.SELECT, cycleId: cycle.id };
}

function determineIdentifiedCycleCursor(
	mouseX: number,
	cycle: CycleData,
	editingCycleId: string | null | undefined,
	videoDuration: number,
	canvasWidth: number
): CursorResult {
	const isSelected = isCycleEditable(cycle, editingCycleId);

	if (cycle.identified && !isSelected) {
		return { cursor: CURSOR_TYPES.POINTER, hoveredCycleId: cycle.id };
	}

	if (!isSelected) {
		return { cursor: CURSOR_TYPES.DEFAULT };
	}

	const check = checkCycleInteraction(mouseX, cycle, videoDuration, canvasWidth);

	if (check.isNearStart || check.isNearEnd) {
		return { cursor: CURSOR_TYPES.EW_RESIZE, hoveredCycleId: cycle.id };
	}

	return { cursor: CURSOR_TYPES.GRAB, hoveredCycleId: cycle.id };
}

export function getCycleAtPosition(
	mouseX: number,
	mouseY: number,
	cycles: CycleData[],
	canvasWidth: number,
	canvasHeight: number,
	videoDuration: number,
	cycleBarHeight: number
): CycleData | null {
	const margin = DEFAULT_CONFIG.MARGIN;

	const overlappingCycles = cycles.filter((cycle) =>
		isWithinCycleBar(mouseX, mouseY, cycle, videoDuration, canvasWidth, canvasHeight, cycleBarHeight, margin)
	);

	if (overlappingCycles.length === 0) {
		return null;
	}

	return getClosestCycle(overlappingCycles, mouseX, videoDuration, canvasWidth);
}

export function detectInteraction(
	mouseX: number,
	cycles: CycleData[],
	canvasWidth: number,
	videoDuration: number,
	permissions: InteractionPermissions
): InteractionResult {
	const margin = DEFAULT_CONFIG.MARGIN;

	const unidentifiedResult = handleUnidentifiedCycle(mouseX, cycles, videoDuration, canvasWidth);

	if (unidentifiedResult?.interaction) {
		return unidentifiedResult.interaction;
	}

	const overlappingCycles = cycles.filter((cycle) => isXWithinCycle(mouseX, cycle, videoDuration, canvasWidth, margin));

	if (overlappingCycles.length === 0) {
		return { type: InteractionType.NONE };
	}

	const targetCycle = getTargetCycle(overlappingCycles, mouseX, videoDuration, canvasWidth, permissions.editingCycleId);
	const isSelected = isCycleEditable(targetCycle, permissions.editingCycleId);

	return determineIdentifiedCycleInteraction(mouseX, targetCycle, isSelected, videoDuration, canvasWidth);
}

export function calculateCursor(
	mouseX: number,
	mouseY: number,
	cycles: CycleData[],
	canvasWidth: number,
	videoDuration: number,
	editingCycleId?: string | null,
	isEditMode = false
): CursorResult {
	const margin = DEFAULT_CONFIG.MARGIN;

	if (mouseY <= DEFAULT_CONFIG.NEEDLE_HANDLE_HEIGHT) {
		return { cursor: CURSOR_TYPES.DEFAULT };
	}
	const unidentifiedResult = handleUnidentifiedCycle(mouseX, cycles, videoDuration, canvasWidth);

	if (unidentifiedResult && unidentifiedResult.cursor) {
		return { cursor: unidentifiedResult.cursor, hoveredCycleId: unidentifiedResult.cycleId };
	}

	const overlappingCycles = cycles.filter((cycle) => isXWithinCycle(mouseX, cycle, videoDuration, canvasWidth, margin));

	if (overlappingCycles.length === 0 || !isEditMode) {
		return { cursor: CURSOR_TYPES.DEFAULT };
	}

	const targetCycle = getTargetCycle(overlappingCycles, mouseX, videoDuration, canvasWidth, editingCycleId);

	return determineIdentifiedCycleCursor(mouseX, targetCycle, editingCycleId, videoDuration, canvasWidth);
}

export function applySnapToCyclePosition(
	start: number,
	end: number,
	isValid: boolean,
	interactionType: InteractionType
): CycleSnapResult {
	if (!isValid) {
		return {
			finalStart: start,
			finalEnd: end,
			snapIndicatorTime: null
		};
	}

	if (
		interactionType === InteractionType.START_RESIZE ||
		interactionType === InteractionType.END_RESIZE ||
		interactionType === InteractionType.MOVE
	) {
		return {
			finalStart: start,
			finalEnd: end,
			snapIndicatorTime: null
		};
	}

	const snapIndicatorTime = {
		finalStart: start,
		finalEnd: end,
		snapIndicatorTime: null
	};

	return snapIndicatorTime;
}

export function determineMouseDownAction(
	interaction: InteractionResult | null,
	isInNeedleHandleArea: boolean,
	isInCycleBarArea: boolean,
	isNearNeedle: boolean,
	_hasUnidentifiedCycle: boolean
): MouseDownAction {
	if (interaction && DRAG_INTERACTIONS.includes(interaction.type) && !isInNeedleHandleArea) {
		return {
			type: MOUSE_ACTION_TYPE.CYCLE_DRAG,
			cycleId: interaction.cycleId,
			shouldPreventDefault: true
		};
	}

	if (isNearNeedle) {
		return {
			type: MOUSE_ACTION_TYPE.NEEDLE_SCRUB,
			shouldPreventDefault: true
		};
	}

	if (interaction && interaction.type === InteractionType.SELECT && isInCycleBarArea) {
		return {
			type: MOUSE_ACTION_TYPE.CYCLE_SELECT,
			cycleId: interaction.cycleId,
			shouldPreventDefault: false
		};
	}

	return {
		type: MOUSE_ACTION_TYPE.EMPTY_SCRUB,
		shouldPreventDefault: true
	};
}
